import React from 'react';
import { BarChart } from '@mui/x-charts/BarChart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWarehouse, faExclamationTriangle, faBan } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

const InventoryWarehouseStock = ({ data }) => {
    const { t } = useTranslation();
    if (!data || !data.warehouses || data.warehouses.length === 0) return null;

    const warehouses = data.warehouses;

    const xLabels = warehouses.map(w => w.warehouse_name);
    const stockData = warehouses.map(w => parseInt(w.total_stock_quantity) || 0);
    const pendingData = warehouses.map(w => parseInt(w.total_pending_orders) || 0);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Warehouse KPI Cards */}
            <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <div style={{ width: 3, height: 16, borderRadius: 2, backgroundColor: '#0891b2' }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                        {t('inventoryAnalytics.charts.warehouseStock.summary')}
                    </span>
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {warehouses.map(w => (
                        <div
                            key={w.warehouse_id}
                            style={{
                                flex: '1 1 180px',
                                minWidth: 180,
                                backgroundColor: '#ffffff',
                                border: '1px solid #a5f3fc',
                                borderRadius: 12,
                                padding: '14px 16px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 8,
                                transition: 'box-shadow 0.15s',
                                cursor: 'default',
                            }}
                            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(165,243,252,0.6)'}
                            onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{w.warehouse_name}</span>
                                <div style={{ width: 28, height: 28, borderRadius: 8, backgroundColor: '#ecfeff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0891b2', fontSize: 12 }}>
                                    <FontAwesomeIcon icon={faWarehouse} />
                                </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                    <span style={{ color: '#6b7280' }}>{t('inventoryAnalytics.charts.warehouseStock.stock')}</span>
                                    <span style={{ fontWeight: 700, color: '#111827' }}>{parseInt(w.total_stock_quantity).toLocaleString('en-IN')}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                    <span style={{ color: '#6b7280' }}>{t('inventoryAnalytics.charts.warehouseStock.pending')}</span>
                                    <span style={{ fontWeight: 600, color: '#d97706' }}>{parseInt(w.total_pending_orders).toLocaleString('en-IN')}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                    <span style={{ color: '#6b7280' }}>{t('inventoryAnalytics.charts.warehouseStock.available')}</span>
                                    <span style={{ fontWeight: 700, color: parseInt(w.available_inventory) < 0 ? '#ef4444' : '#059669' }}>
                                        {parseInt(w.available_inventory).toLocaleString('en-IN')}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                    <span style={{ color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#f59e0b', fontSize: 10 }} /> {t('inventoryAnalytics.charts.warehouseStock.lowStockLabel')}
                                    </span>
                                    <span style={{ fontWeight: 600, color: '#dc2626' }}>{w.low_stock_count.toLocaleString('en-IN')}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                    <span style={{ color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <FontAwesomeIcon icon={faBan} style={{ color: '#ef4444', fontSize: 10 }} /> {t('inventoryAnalytics.charts.warehouseStock.outOfStockLabel')}
                                    </span>
                                    <span style={{ fontWeight: 600, color: '#dc2626' }}>{w.out_of_stock_count.toLocaleString('en-IN')}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Chart */}
            <div style={cardStyle}>
                <div style={headerStyle}>
                    <div>
                        <span style={titleStyle}>{t('inventoryAnalytics.charts.warehouseStock.title')}</span>
                        <div style={subtitleStyle}>{t('inventoryAnalytics.charts.warehouseStock.subtitle')}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 16 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: '#3b82f6' }} />
                            <span style={{ fontSize: 12, color: '#6b7280' }}>{t('inventoryAnalytics.charts.warehouseStock.headers.stock')}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: '#f59e0b' }} />
                            <span style={{ fontSize: 12, color: '#6b7280' }}>{t('inventoryAnalytics.charts.warehouseStock.headers.pending')}</span>
                        </div>
                    </div>
                </div>
                <div style={{ padding: '0 8px 16px', height: 260 }}>
                    <BarChart
                        series={[
                            { data: stockData, label: t('inventoryAnalytics.charts.warehouseStock.headers.stock'), color: '#3b82f6' },
                            { data: pendingData, label: t('inventoryAnalytics.charts.warehouseStock.headers.pending'), color: '#f59e0b' },
                        ]}
                        xAxis={[{ scaleType: 'band', data: xLabels, tickLabelStyle: { fontSize: 12, fill: '#9ca3af' } }]}
                        yAxis={[{ tickLabelStyle: { fontSize: 10, fill: '#9ca3af' } }]}
                        slotProps={{ legend: { hidden: true } }}
                        margin={{ top: 10, right: 20, bottom: 40, left: 60 }}
                        borderRadius={4}
                    />
                </div>
            </div>

            {/* Table */}
            <div style={cardStyle}>
                <div style={headerStyle}>
                    <div>
                        <span style={titleStyle}>{t('inventoryAnalytics.charts.warehouseStock.detailsTitle')}</span>
                        <div style={subtitleStyle}>{t('inventoryAnalytics.charts.warehouseStock.detailsSubtitle')}</div>
                    </div>
                    <span style={badgeStyle}>{t('inventoryAnalytics.charts.warehouseStock.warehousesCount', { count: warehouses.length })}</span>
                </div>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                                <th style={thStyle('left')}>{t('inventoryAnalytics.charts.warehouseStock.headers.warehouse')}</th>
                                <th style={thStyle('right')}>{t('inventoryAnalytics.charts.warehouseStock.headers.products')}</th>
                                <th style={thStyle('right')}>{t('inventoryAnalytics.charts.warehouseStock.headers.stock')}</th>
                                <th style={thStyle('right')}>{t('inventoryAnalytics.charts.warehouseStock.headers.pending')}</th>
                                <th style={thStyle('right')}>{t('inventoryAnalytics.charts.warehouseStock.headers.available')}</th>
                                <th style={thStyle('right')}>{t('inventoryAnalytics.charts.warehouseStock.headers.lowStock')}</th>
                                <th style={thStyle('right')}>{t('inventoryAnalytics.charts.warehouseStock.headers.outOfStock')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {warehouses.map((w, i) => (
                                <tr
                                    key={w.warehouse_id || i}
                                    style={{ borderBottom: '1px solid #f9fafb' }}
                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                    <td style={{ ...tdStyle, fontWeight: 600, color: '#111827' }}>{w.warehouse_name}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right', color: '#6b7280' }}>{w.total_products.toLocaleString('en-IN')}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: '#111827' }}>{parseInt(w.total_stock_quantity).toLocaleString('en-IN')}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: '#d97706' }}>{parseInt(w.total_pending_orders).toLocaleString('en-IN')}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: parseInt(w.available_inventory) < 0 ? '#ef4444' : '#059669' }}>
                                        {parseInt(w.available_inventory).toLocaleString('en-IN')}
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                                        <span style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', backgroundColor: '#fef2f2', borderRadius: 12, padding: '2px 8px' }}>
                                            {w.low_stock_count.toLocaleString('en-IN')}
                                        </span>
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                                        <span style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', backgroundColor: '#fef2f2', borderRadius: 12, padding: '2px 8px' }}>
                                            {w.out_of_stock_count.toLocaleString('en-IN')}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const cardStyle = {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    border: '1px solid #f3f4f6',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    overflow: 'hidden',
};
const headerStyle = {
    padding: '18px 20px 12px',
    borderBottom: '1px solid #f9fafb',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
};
const titleStyle = { fontSize: 15, fontWeight: 700, color: '#111827', display: 'block' };
const subtitleStyle = { fontSize: 12, color: '#9ca3af', marginTop: 2 };
const badgeStyle = {
    fontSize: 11, fontWeight: 700, color: '#7c3aed',
    backgroundColor: '#f5f3ff', borderRadius: 20, padding: '3px 10px',
};
const thStyle = (align) => ({
    padding: '10px 16px',
    textAlign: align,
    fontSize: 11,
    fontWeight: 700,
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    backgroundColor: '#fafafa',
    whiteSpace: 'nowrap',
});
const tdStyle = { padding: '10px 16px', verticalAlign: 'middle' };

export default InventoryWarehouseStock;
